import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Folder, Sparkles, TrendingUp, Users, AlertCircle, DollarSign, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getCommandCenter, getActivityFeed, listMyLenses } from "@/lib/command-center.functions";
import { listMyWorkspaces } from "@/lib/workspaces.functions";
import { FeatureGate } from "@/components/feature-gate";
import { LensesInner } from "./lenses";
import { ProgramComparisonInner } from "./intelligence.program-comparison";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/command-center")({
  component: CommandCenter,
});

type Period = "today" | "week" | "month" | "quarter" | "year" | "all";

function periodRange(p: Period): { from: string; to: string; label: string } {
  const now = new Date();
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  const from = new Date(now);
  switch (p) {
    case "today": from.setHours(0, 0, 0, 0); break;
    case "week": from.setDate(now.getDate() - 7); break;
    case "month": from.setMonth(now.getMonth() - 1); break;
    case "quarter": from.setMonth(now.getMonth() - 3); break;
    case "year": from.setFullYear(now.getFullYear() - 1); break;
    case "all": from.setFullYear(2000); break;
  }
  return { from: from.toISOString(), to: to.toISOString(), label: p };
}

const PERIODS: { value: Period; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
];

function fmtMoney(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtPct(v: number | null | undefined): string {
  return `${Number(v ?? 0).toFixed(1)}%`;
}

function Tile({ label, value, sub, icon: Icon, trend }: { label: string; value: string; sub?: React.ReactNode; icon: any; trend?: number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="text-3xl font-mono font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
        {trend != null && (
          <div className={cn("text-xs mt-1.5 flex items-center gap-1", trend >= 0 ? "text-green-600" : "text-red-600")}>
            {trend >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {Math.abs(trend).toFixed(1)}% vs prev
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CommandCenter() {
  const [period, setPeriod] = useState<Period>("month");
  const [lensId, setLensId] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"dashboard" | "comparison">("dashboard");
  const [lensManagerOpen, setLensManagerOpen] = useState(false);
  const range = useMemo(() => periodRange(period), [period]);

  const fetchCC = useServerFn(getCommandCenter);
  const fetchFeed = useServerFn(getActivityFeed);
  const fetchWs = useServerFn(listMyWorkspaces);
  const fetchLenses = useServerFn(listMyLenses);

  const lensParam = lensId === "all" ? null : lensId;

  const { data: ccData, isLoading } = useQuery({
    queryKey: ["command-center", period, lensId],
    queryFn: () => fetchCC({ data: { from: range.from, to: range.to, lens_id: lensParam } }),
  });
  const { data: feedData } = useQuery({
    queryKey: ["activity-feed", "all", lensId],
    queryFn: () => fetchFeed({ data: { filter: "all", lens_id: lensParam, limit: 25 } }),
  });
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });
  const { data: lensesData } = useQuery({ queryKey: ["my-lenses"], queryFn: () => fetchLenses() });
  const lenses = lensesData?.lenses ?? [];

  const metrics = (ccData?.metrics as any) ?? null;
  const hero = metrics?.hero ?? {};
  const byProgram: any[] = metrics?.by_program ?? [];
  const topCustomers: any[] = metrics?.top_customers ?? [];
  const hasRecurring: boolean = metrics?.has_recurring ?? false;
  const workspaceCount = wsData?.workspaces?.length ?? 0;
  const trend = hero.prev_total_revenue ? ((hero.total_revenue - hero.prev_total_revenue) / hero.prev_total_revenue) * 100 : null;

  if (workspaceCount === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Sparkles className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <div className="text-lg font-medium mb-1">Welcome to your Command Center</div>
            <p className="text-sm text-muted-foreground mb-4">Add your first affiliate program to get started.</p>
            <Button asChild><Link to="/dashboard">Add Affiliate Program</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Command Center</h1>
          <p className="text-sm text-muted-foreground">All your programs, one view.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "dashboard" | "comparison")}>
            <TabsList>
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="comparison">Comparison</TabsTrigger>
            </TabsList>
          </Tabs>
          {viewMode === "dashboard" && (
            <>
              <Select
                value={lensId}
                onValueChange={(v) => {
                  if (v === "__manage__") { setLensManagerOpen(true); return; }
                  setLensId(v);
                }}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="View: All Programs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  {lenses.map((l: any) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                  <SelectSeparator />
                  <SelectItem value="__manage__">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Settings2 className="h-3.5 w-3.5" /> Manage Lenses…
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>

      <Dialog open={lensManagerOpen} onOpenChange={setLensManagerOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Lenses</DialogTitle></DialogHeader>
          <LensesInner />
        </DialogContent>
      </Dialog>

      {viewMode === "comparison" ? (
        <ProgramComparisonInner showHeader={false} />
      ) : (
        <>

      {workspaceCount === 1 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="text-sm">Add another affiliate program to unlock cross-program intelligence.</div>
            <Button asChild size="sm" variant="outline"><Link to="/dashboard">+ Add Program</Link></Button>
          </CardContent>
        </Card>
      )}

      {/* Hero tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile label="Total Revenue" value={isLoading ? "," : fmtMoney(hero.total_revenue)} icon={DollarSign} trend={trend ?? undefined} />
        {hasRecurring && <Tile label="MRR" value={isLoading ? "," : fmtMoney(hero.mrr)} icon={TrendingUp} />}
        {Number(hero.one_time_revenue ?? 0) > 0 && (
          <Tile label="One-Time Revenue" value={fmtMoney(hero.one_time_revenue)} icon={DollarSign} />
        )}
        <Tile label="Active Customers" value={String(hero.active_customers ?? 0)} icon={Users} sub="Deduped across programs" />
        {hasRecurring && <Tile label="Churn Rate" value={fmtPct(hero.churn_rate)} icon={AlertCircle} />}
        <Tile label="Net New Revenue" value={fmtMoney(hero.net_new_revenue)} icon={TrendingUp} sub="vs previous period" />
      </div>

      {/* By Program grid */}
      <div>
        <h2 className="text-lg font-semibold mb-3">By Program</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {byProgram.map((p) => (
            <Link key={p.workspace_id} to="/workspaces/$workspaceId" params={{ workspaceId: p.workspace_id } as any}>
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Folder className="h-4 w-4 text-muted-foreground" />
                    <div className="font-medium">{p.program_name}</div>
                  </div>
                  <div className="text-2xl font-mono font-semibold">{fmtMoney(p.revenue)}</div>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    {p.mrr > 0 && <span>MRR {fmtMoney(p.mrr)}</span>}
                    <span>{p.active ?? 0} active</span>
                    <span>+{p.new_this_period ?? 0} new</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Activity feed */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Activity</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
            {(feedData?.events ?? []).length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">No activity yet.</div>
            )}
            {(feedData?.events ?? []).map((e: any) => {
              const stripe = e.event_type === "cross_program_churn_alert" ? "bg-amber-500"
                : e.event_type === "churn_detected" ? "bg-red-500"
                : e.event_type === "anomaly_flagged" ? "bg-amber-500"
                : "bg-muted-foreground/30";
              return (
                <div key={e.id} className="flex gap-3 p-2 rounded hover:bg-muted/50">
                  <div className={cn("w-1 rounded-full", stripe)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {e.program_name && <Badge variant="outline" className="text-[10px]">{e.program_name}</Badge>}
                      <span>{new Date(e.occurred_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-sm mt-0.5">{e.description}</div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Top customers (gated) */}
        <FeatureGate feature="top_customers_leaderboard">
          <Card>
            <CardHeader><CardTitle className="text-base">Top Customers</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {topCustomers.length === 0 && (
                <div className="text-sm text-muted-foreground py-4 text-center">No customers yet.</div>
              )}
              {topCustomers.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.merged_name || c.primary_email}</div>
                    <div className="text-xs text-muted-foreground truncate">{(c.programs ?? []).join(" · ")}</div>
                  </div>
                  <div className="text-sm font-mono font-semibold">{fmtMoney(c.total_ltv)}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </FeatureGate>
      </div>
        </>
      )}
    </div>
  );
}
